import { useMemo } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { customReportsApi } from '@/services/customReports.service';
import { aiReportsApi } from '@/services/aiReports.service';
import { standardReportsService } from '@/services/standardReports.service';
import {
  formatFilterTextOneLine,
  orderIdOf,
  resultIdOf,
} from '@/pages/reports/resultHelpers';
import type { CstOrder, ReportResult } from '@/types/customReports.types';

/**
 * One list of every report a user can see, whatever produced it.
 *
 * WHY THE MERGE HAPPENS HERE AND NOT IN SQL. The three kinds live in tables on
 * two different connections that the API deliberately never joins across —
 * `standard_report_orders` and the legacy `reports_cst_*` tables sit on the v4
 * connection, `standard_report_runs` and `ai_report_runs` on the default one,
 * and the schema files say in as many words that no transaction spans the two.
 * A union query would have to bridge that, and would be new untested SQL against
 * a database this repo's test suite cannot reach.
 *
 * Merging the existing endpoints instead means every source here already carries
 * production traffic. The pattern is not new either: the Scheduled Reports page
 * this replaces already merged three of these five client-side.
 *
 * ONE ROW PER REPORT, not per run. A weekly report is one row however many times
 * it has fired; its runs hang off it, which is what gives pause/resume somewhere
 * to live.
 *
 * A REPORT APPEARS ONCE IT HAS PRODUCED SOMETHING. An earlier version listed
 * report DEFINITIONS so that a never-run report would still show — which on a
 * real account meant hundreds of dormant legacy definitions, all identically
 * named, all falsely labelled "Never run". Listing what has actually run is both
 * the legacy behaviour and the useful one. The trade is real and deliberate: a
 * scheduled report that has never fired is not listed here.
 */

export type ReportKind = 'standard' | 'custom' | 'ai';

/**
 * Set only while a report is waiting on its first run. `null` on every row that
 * has produced a result, which is what the Status column keys off.
 */
export type PendingState = 'preparing' | 'scheduled' | null;

export interface MyReportRow {
  /** Stable across kinds — the id alone collides between tables. */
  _key: string;
  kind: ReportKind;
  id: number;
  title: string;
  /** Which report this is, under the user's name for it. */
  reportName: string;
  /** The sub-report beneath the family, when the run recorded one. */
  subReport: string | null;
  /**
   * One-line summary of the filters the run used. This is what distinguishes a
   * hundred reports all called "Patients"; without it the list is unreadable,
   * which is exactly what shipped.
   */
  filterSummary: string | null;
  recurring: boolean;
  cadence: string | null;
  nextRun: string | null;
  lastRun: string | null;
  /** Null when it has never run — rendered as such, never as a zero. */
  lastRunStatus: string | null;
  runCount: number | null;
  active: boolean;
  sharedWithMe: boolean;
  /** Server-side ownership rules mean some rows cannot be acted on. */
  canManage: boolean;
  /**
   * Whether this row can be fired on demand. NOT the same as `canManage`: a
   * standard report run once from the catalog has a run but no order behind it,
   * so there is nothing for "run now" to address — it would have to be ordered
   * again from scratch. Computed here, where the row's origin is still known.
   */
  canRunNow: boolean;
  /** Where "Edit" goes, or null when the kind has no editor. */
  editPath: string | null;
  /**
   * Where opening the row goes. Computed per source rather than assembled by
   * the page from `kind` and `id`, because within one kind `id` does not mean
   * one thing: a standard row carries an ORDER id when it came from a saved
   * schedule and a RUN id when it came from a bare catalog run. The page built
   * `/reports/my-reports/{kind}/{id}` for both, so the run case asked the runs
   * endpoint for `orderId = <a run id>` and every such report opened empty.
   * Only the loop that emitted the row knows which id it put there.
   */
  openPath: string;
  /** Where opening the row goes when there is a result to open. */
  latestResultId: number | null;
  createdAt: string | null;
  /**
   * Whether this row is still waiting on its first run, and in which sense.
   * A separate field rather than a value smuggled through `lastRunStatus`:
   * that one is rendered raw as the badge's text, so anything put there
   * becomes user-facing copy.
   */
  pending: PendingState;
}

const KEYS = {
  standardOrders: ['reports', 'standard-orders'] as const,
  standardRuns: ['reports', 'standard-runs'] as const,
  customResults: ['reports', 'results', 'cst', 'grouped'] as const,
  customScheduled: ['reports', 'scheduled'] as const,
  aiRuns: ['reports', 'ai-runs'] as const,
};

/**
 * Mark every source this list is assembled from as stale.
 *
 * EXPORTED because the things that ADD a report do not happen on this page —
 * they happen in the builder wizard, the standard runner and the AI modals, and
 * each was left to remember these keys for itself. None of them did: the wizard
 * invalidated two keys that match no query at all (`reports/onDemand`), the AI
 * screens invalidated `reports/ai-list` which likewise matches nothing, and the
 * standard runner and its schedule dialog invalidated nothing whatsoever. With
 * every source held for 30s, that meant a report a user had just created was
 * simply absent from the list until they reloaded the browser.
 *
 * One function, so a new source added to KEYS is picked up by every caller
 * rather than needing six call sites updated in step.
 */
export function invalidateMyReports(qc: QueryClient): void {
  for (const key of Object.values(KEYS)) {
    void qc.invalidateQueries({ queryKey: key });
  }
}

function cadenceLabel(unit: string | null, count: number): string | null {
  if (!unit) return null;
  if (count === 1) return `Every ${unit}`;

  return `Every ${count} ${unit}s`;
}

/**
 * How long a report that has been ordered but has not run stays listed.
 *
 * The bound is the whole design, not a tuning knob. This list is built from
 * RESULTS so that the dormant legacy definitions stay out of it (see the note
 * at the top of this file). Emitting a row for every order without a result
 * would put all of them back. Recency is the discriminator that separates the
 * two populations: a report someone ordered minutes ago is worth showing before
 * it has run; one ordered long ago and still empty is the dormant case.
 *
 * Day-granular in practice: `dateCreated` is stored without a time, so this
 * behaves as "ordered today" rather than a rolling 24 hours.
 */
export const PENDING_WINDOW_HOURS = 24;

/** How often the list re-checks while something is still waiting. */
const PENDING_POLL_MS = 5_000;

/**
 * Milliseconds for a legacy date or datetime, or null when there is not a real
 * date there — legacy stores "no date" as null, as '', and as the zero date.
 *
 * Both shapes turn up, and they must not be parsed the same way.
 * `ReportsScheduledController::store` writes `dateCreated` as 'Y-m-d' with no
 * time, and a date-only string is parsed as UTC per spec — which back-dates it
 * by the viewer's offset, seven hours in Pacific. Left alone, that made a
 * report ordered after ~17:00 compute as over a day old, so the row this file
 * exists to show would silently not appear for the rest of every evening.
 * Pinning both shapes to local time keeps the comparison in one frame.
 */
function parsedTime(value: string | null | undefined): number | null {
  if (!value || value.startsWith('0000-00-00')) return null;

  const local = value.includes(' ') ? value.replace(' ', 'T') : `${value}T00:00:00`;
  const t = new Date(local).getTime();

  return Number.isNaN(t) ? null : t;
}

/**
 * Whether an order is waiting on its FIRST run and was ordered recently enough
 * to still be worth listing.
 *
 * False for anything undated, deliberately: an order whose `dateCreated` cannot
 * be read cannot be shown to be recent, and that is precisely the dormant
 * population this must not admit.
 */
export function isCustomOrderInFlight(order: CstOrder, now = new Date()): boolean {
  if (!Number(order.active ?? 0)) return false;
  // Has run before, so its state belongs to a result row, not a pending one.
  if (Number(order.processCount ?? 0) > 0) return false;
  if (parsedTime(order.lastDate) !== null) return false;

  const created = parsedTime(order.dateCreated);
  if (created === null) return false;

  return now.getTime() - created <= PENDING_WINDOW_HOURS * 3_600_000;
}

/** Dated ahead reads as scheduled; due now or overdue reads as preparing. */
function pendingStateFor(order: CstOrder, now = new Date()): PendingState {
  const due = parsedTime(order.deliveryDate);

  return due !== null && due > now.getTime() ? 'scheduled' : 'preparing';
}

/**
 * Whether anything is still waiting — what the refresh is gated on.
 *
 * Takes both lists because an order stops being interesting the moment its
 * result exists, and the two come from different endpoints.
 */
export function hasInFlightCustomOrder(
  orders: CstOrder[],
  results: ReportResult[],
  now = new Date(),
): boolean {
  const settled = new Set(results.map((r) => orderIdOf(r)));

  return orders.some(
    (o) => !settled.has(Number(o.reportsCustomOrdersID)) && isCustomOrderInFlight(o, now),
  );
}

export function useMyReports() {
  const qc = useQueryClient();

  // Each source is independent and `retry: false`: a 403 on the AI endpoints
  // (they need the AI Assistant permission, id 26) must read as "no AI reports"
  // rather than failing the whole list. Same handling the page this replaces
  // used, for the same reason.
  const standardOrders = useQuery({
    queryKey: KEYS.standardOrders,
    queryFn: standardReportsService.listOrders,
    staleTime: 30_000,
    retry: false,
  });

  const standardRuns = useQuery({
    queryKey: KEYS.standardRuns,
    queryFn: () => standardReportsService.listRuns({ limit: 200 }),
    staleTime: 30_000,
    retry: false,
  });

  // SCHEDULED RESULTS, NOT DEFINITIONS — and deliberately not `overview`.
  //
  // This was built on `customReportsApi.overview()`, which returns report
  // DEFINITIONS with no LIMIT and no dedupe. Two things made that catastrophic
  // here. First, the V5 wizard only ever writes `cst` (scheduled) orders — see
  // ReportBuilderWizard, which always calls scheduled.create/update — so every
  // `cdm` row in that response is residue from the legacy PHP app, where staff
  // saved a fresh definition each time they wanted an ad-hoc pull. Hundreds of
  // rows all named "Patients". Second, a definition carries no run history, so
  // every one of them rendered "Never run" whether or not it had ever produced
  // anything.
  //
  // `groupByOrder` returns the latest RESULT per order instead: one row per
  // report that has actually produced something, carrying the real generated
  // date, the filters it ran with, and the report subtype. It is what the legacy
  // list does and what the page this replaced did.
  //
  // Consequence, accepted deliberately: a scheduled report that has never run
  // does not appear here.
  // Refresh only while something is actually waiting, then stop. Nothing here
  // polled at all before, so a report finishing while the page was open stayed
  // invisible until a reload — the list the wizard sends people to appeared not
  // to be doing anything. Read from the cache rather than from `rows`: that is
  // memoised further down, after these queries are declared.
  const pendingPollMs = (): number | false =>
    hasInFlightCustomOrder(
      qc.getQueryData<CstOrder[]>(KEYS.customScheduled) ?? [],
      qc.getQueryData<ReportResult[]>(KEYS.customResults) ?? [],
    )
      ? PENDING_POLL_MS
      : false;

  const customResults = useQuery({
    queryKey: KEYS.customResults,
    queryFn: () => customReportsApi.results.list({ type: 'cst', groupByOrder: true }),
    staleTime: 30_000,
    retry: false,
    refetchInterval: pendingPollMs,
  });

  const customScheduled = useQuery({
    queryKey: KEYS.customScheduled,
    queryFn: customReportsApi.scheduled.list,
    staleTime: 30_000,
    retry: false,
    refetchInterval: pendingPollMs,
  });

  // One source, not two: `list()` already merges AI schedules and one-off runs
  // server-side and marks each row with `scheduled` / `recurring` / `scheduleId`.
  // Querying the schedules endpoint as well would double-count every recurring
  // AI report.
  const aiReports = useQuery({
    queryKey: KEYS.aiRuns,
    queryFn: aiReportsApi.list,
    staleTime: 30_000,
    retry: false,
  });

  const rows = useMemo<MyReportRow[]>(() => {
    const out: MyReportRow[] = [];

    // ── Standard: saved orders ────────────────────────────────────────────
    const orders = standardOrders.data ?? [];

    for (const o of orders) {
      out.push({
        _key: `standard-order:${o.orderId}`,
        kind: 'standard',
        id: o.orderId,
        title: o.title || o.reportKey,
        reportName: o.reportKey,
        subReport: null,
        filterSummary: null,
        recurring: o.recurring,
        cadence: o.recurring ? cadenceLabel(o.cadenceUnit, o.cadenceCount) : null,
        nextRun: o.recurring && o.active ? o.nextRunAt : null,
        lastRun: o.lastRunAt,
        lastRunStatus: null,
        runCount: o.runCount,
        active: o.active,
        sharedWithMe: false,
        canManage: true,
        canRunNow: true,
        editPath: null,
        // An order id, which is what the runs page filters on.
        openPath: `/reports/my-reports/standard/${o.orderId}`,
        latestResultId: o.lastRunId,
        createdAt: null,
        pending: null,
      });
    }

    // ── Standard: one-off runs with no order behind them ──────────────────
    // A report run straight from the catalog creates a run and no order, so it
    // would be invisible in a list built only from definitions.
    //
    // WHAT IS SKIPPED IS "BELONGS TO A LISTED ORDER", NOT "IS THE LATEST RUN".
    // This filtered on `o.lastRunId`, so every EARLIER run of a saved schedule
    // — including the failure rows the scheduler writes — came through as its
    // own row labelled "One-off". A weekly schedule that had fired four times
    // listed itself once and then three more times as something it isn't.
    //
    // A run whose order is NOT listed still gets a row: the order may have been
    // deleted, or withheld from this caller while the run itself is shared, and
    // in both cases the run is the only thing left to reach it by.
    const listedStandardOrderIds = new Set(orders.map((o) => o.orderId));

    for (const r of standardRuns.data ?? []) {
      if (r.orderId !== null && listedStandardOrderIds.has(r.orderId)) continue;

      out.push({
        _key: `standard-run:${r.runId}`,
        kind: 'standard',
        id: r.runId,
        title: r.reportKey,
        reportName: r.reportKey,
        subReport: null,
        filterSummary: null,
        recurring: false,
        cadence: null,
        nextRun: null,
        lastRun: r.completedAt ?? r.createdAt,
        lastRunStatus: r.status,
        runCount: 1,
        active: true,
        sharedWithMe: false,
        canManage: true,
        // No order behind it, so there is nothing to re-fire.
        canRunNow: false,
        editPath: null,
        // Straight to the run. There is no order to list runs under, and this
        // row IS one run — the runs page could only ever show this single row.
        openPath: `/reports/standard-runs/${r.runId}`,
        latestResultId: r.runId,
        createdAt: r.createdAt,
        pending: null,
      });
    }

    // ── Custom: one row per scheduled report that has produced a result ───
    // The schedule (cadence, next delivery, paused) lives on the ORDER, and the
    // run facts (when, with which filters, which sub-report) live on the RESULT.
    // Neither alone can fill a row, so the result carries the row and the order
    // is joined onto it.
    const scheduledById = new Map(
      (customScheduled.data ?? []).map((s) => [Number(s.reportsCustomOrdersID), s]),
    );

    for (const r of customResults.data ?? []) {
      const orderId = orderIdOf(r);
      const sched = scheduledById.get(orderId);

      out.push({
        _key: `custom-cst:${orderId}`,
        kind: 'custom',
        id: orderId,
        title: r.title || r.reportTitle || 'Report',
        // The SUBTYPE, not the family name. "Patients" on its own says nothing
        // when a hundred reports are called Patients; this is the sub-report the
        // run actually produced.
        reportName: r.reportTitle || r.selectedReport || 'Custom report',
        subReport: r.subFiltersText ?? null,
        // What it ran with — the only thing that tells two same-named reports
        // apart. Decoded by the parser that already exists for the legacy
        // `filterText` storage format.
        filterSummary: formatFilterTextOneLine(r.filterText) || null,
        recurring: Boolean(sched?.recurring),
        cadence: sched?.deliverEvery ? `Every ${sched.deliverEvery}` : null,
        nextRun: sched?.deliveryDate ?? null,
        // The real generated date, off the result. The order's `lastDate` is
        // cron-maintained at best and was the source of the false "Never run".
        lastRun: r.dateCreated ?? null,
        lastRunStatus: null,
        runCount: sched?.processCount ?? null,
        active: sched ? Boolean(Number(sched.active)) : true,
        sharedWithMe: r.isOwner === false,
        canManage: r.isOwner !== false,
        // Scheduled orders are fired by the cron, not on demand.
        canRunNow: false,
        editPath: `/reports/orders/${orderId}/edit`,
        openPath: `/reports/my-reports/custom/${orderId}`,
        latestResultId: resultIdOf(r),
        createdAt: r.dateCreated ?? null,
        pending: null,
      });
    }

    // ── Custom: ordered, waiting on its first run ─────────────────────────
    // The pass above lists what has PRODUCED something. That is right for the
    // hundreds of dormant legacy definitions and wrong for the report the user
    // ordered seconds ago: it has no result yet, so it was absent from the very
    // list the wizard sends them to, and the submission looked like it had done
    // nothing. `isCustomOrderInFlight` is what keeps the two apart — only
    // recently-ordered, never-run orders join here.
    const listedOrderIds = new Set((customResults.data ?? []).map((r) => orderIdOf(r)));

    for (const o of customScheduled.data ?? []) {
      const orderId = Number(o.reportsCustomOrdersID);

      if (listedOrderIds.has(orderId)) continue;
      if (!isCustomOrderInFlight(o)) continue;

      out.push({
        _key: `custom-cst:${orderId}`,
        kind: 'custom',
        id: orderId,
        title: o.title || o.reportTitle || 'Report',
        reportName: o.reportTitle || o.reportSelect || 'Custom report',
        subReport: o.subFiltersText ?? null,
        // The order carries `filterText` too, so a pending row is just as
        // distinguishable as a run one — which matters most here, where the
        // name is shared with everything else the user has ever ordered.
        filterSummary: formatFilterTextOneLine(o.filterText) || null,
        recurring: Boolean(Number(o.recurring ?? 0)),
        cadence: o.deliverEvery ? `Every ${o.deliverEvery}` : null,
        nextRun: o.deliveryDate ?? null,
        // Never run: the pending badge carries the state, not a faked date.
        lastRun: null,
        lastRunStatus: null,
        runCount: null,
        active: true,
        sharedWithMe: false,
        canManage: true,
        canRunNow: false,
        editPath: `/reports/orders/${orderId}/edit`,
        openPath: `/reports/my-reports/custom/${orderId}`,
        // Nothing to open yet; the detail page has an empty state for this.
        latestResultId: null,
        createdAt: o.dateCreated ?? null,
        pending: pendingStateFor(o),
      });
    }

    // ── AI: schedules and one-off runs, already merged by the endpoint ────
    for (const r of aiReports.data ?? []) {
      // A schedule row is identified by its scheduleId; a bare run has none.
      // The id has to follow suit or pause/resume would address the wrong thing.
      const isSchedule = r.scheduleId !== null;

      out.push({
        _key: isSchedule ? `ai-schedule:${r.scheduleId}` : `ai-run:${r.id}`,
        kind: 'ai',
        id: (isSchedule ? r.scheduleId : r.id) ?? 0,
        title: r.title || 'AI report',
        reportName: 'Asked in chat',
        subReport: null,
        filterSummary: null,
        recurring: r.recurring,
        cadence: null,
        nextRun: r.nextRunLocal,
        lastRun: r.createdAt,
        lastRunStatus: r.status,
        runCount: null,
        active: true,
        sharedWithMe: r.shared,
        // AI mutations are owner-only server-side (403 otherwise), so a shared
        // row offers no actions rather than offering ones that will be refused.
        canManage: !r.shared,
        // Only a schedule can be fired again; a bare run is a finished answer.
        canRunNow: isSchedule && !r.shared,
        editPath: null,
        openPath: `/reports/my-reports/ai/${(isSchedule ? r.scheduleId : r.id) ?? 0}`,
        latestResultId: r.id,
        createdAt: r.createdAt,
        pending: null,
      });
    }

    // Most recently active first. A report that has never run sorts to the
    // bottom rather than the top, where a null date would otherwise put it.
    return out.sort((a, b) => {
      const at = a.lastRun ?? a.createdAt ?? '';
      const bt = b.lastRun ?? b.createdAt ?? '';

      return bt.localeCompare(at);
    });
  }, [
    standardOrders.data,
    standardRuns.data,
    customResults.data,
    customScheduled.data,
    aiReports.data,
  ]);

  const invalidate = () => invalidateMyReports(qc);

  return {
    rows,
    // Loading only while nothing is showable yet — one slow source must not
    // hold back the four that already answered.
    loading:
      standardOrders.isLoading &&
      standardRuns.isLoading &&
      customResults.isLoading &&
      aiReports.isLoading,
    invalidate,
  };
}
